import { useEffect, useRef } from 'react';

// Quote builder forms commit a change by posting the whole values bag and rendering what comes back.
// A picker commits the moment it changes -- selects, checkboxes and the unit pickers all bump
// `updated_at`, which the form watches. A TYPED value had no such moment: it only committed when the
// form's onBlur fired, so somebody who typed a number and looked straight at the total was reading
// the total for the old number until they clicked away.
//
// This gives typing the moment it was missing. A pause commits, and Enter commits at once.
//
// Nothing commits until the user has actually changed something. Mounting a form writes to its values
// several times over -- defaults, listen_conditions, readOnlyValues -- and a page carries several
// forms, so committing those would cost a round trip each for changes nobody made.
const useCommitOnPause = ({ values, enabled, commit, delay = 500 }) => {
  const userHasChangedSomething = useRef(false);
  const pendingCommit = useRef(null);
  // Held in a ref because the caller rebuilds `commit` on every render; the timer must run the
  // newest one, not the one that existed when it was scheduled.
  const latestCommit = useRef(commit);
  latestCommit.current = commit;

  const armed = () => enabled && userHasChangedSomething.current;

  // Wire to the form element: any input event anywhere inside it means the user is editing.
  const noteUserChange = () => {
    userHasChangedSomething.current = true;
  };

  useEffect(() => {
    if (!armed()) { return undefined; }

    pendingCommit.current = setTimeout(() => {
      pendingCommit.current = null;
      latestCommit.current();
    }, delay);

    return () => clearTimeout(pendingCommit.current);
  }, [values]);

  // Enter means "now": commit without waiting out the pause, and drop the pause that was already
  // running so the same change is not posted twice.
  const commitNow = () => {
    if (!armed()) { return; }

    clearTimeout(pendingCommit.current);
    pendingCommit.current = null;
    latestCommit.current();
  };

  return { noteUserChange, commitNow };
};

export default useCommitOnPause;
